import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Checkbox,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Link,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import callApi from "../../../../services/callApi";
import logo from "../../../../assets/images/logo.png";
import { isMobile } from "react-device-detect";

// Componente estilizado para el formulario
const FormContainer = styled(Paper)(({ theme }) => ({
  maxWidth: 600,
  margin: "0 auto",
  padding: theme.spacing(3),
  borderRadius: 10,
  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
  position: "relative",
  overflow: "hidden",
}));

// Componente para el encabezado con logo
const FormHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  marginBottom: theme.spacing(4),
  borderBottom: "1px solid #e0e0e0",
  paddingBottom: theme.spacing(2),
}));

// Componente para las preguntas
const QuestionContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  padding: theme.spacing(2),
  borderRadius: 8,
  border: "1px solid #e0e0e0",
  position: "relative",
  // Add these properties to prevent text overflow
  overflowWrap: "break-word",
  wordWrap: "break-word",
  wordBreak: "break-word",
  hyphens: "auto",
}));

// Componente para el indicador de pregunta obligatoria
const RequiredIndicator = styled(Box)({
  color: "#FF5252",
  fontWeight: "bold",
  display: "inline",
});

// Componente estilizado para botón de enviar
const SubmitButton = styled(Button)(({ theme }) => ({
  fontSize: { xs: "14px", md: "16px", lg: "16px" },
  fontFamily: "Quicksand-Bold",
  backgroundColor: "#8753b1",
  "&:hover": { backgroundColor: "#651d87" },
  borderRadius: 3,
  width: "100%",
  color: "white",
  textTransform: "none",
  "&.Mui-disabled": {
    backgroundColor: "#E5E7EB",
    color: "#9CA3AF",
  },
}));

const FormularioRespuesta = () => {
  const { id } = useParams();
  const [formulario, setFormulario] = useState(null);
  const [respuestas, setRespuestas] = useState({});
  const [loading, setLoading] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const [exito, setExito] = useState(false);
  const [id_formulario_asignado, setId] = useState(null);
  const [estadoFormulario, setEstadoFormulario] = useState(null);

  // Cargar datos del formulario
  useEffect(() => {
    const obtenerFormulario = async () => {
      try {
        setLoading(true);
        setError(null);

        // Determinar qué parámetro usar basado en el formato del ID
        let queryParam = "";

        if (
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
            id
          )
        ) {
          queryParam = `transferencia_id=${id}`;
        } else if (id.startsWith("pref_")) {
          queryParam = `preference_id=${id}`;
        } else {
          queryParam = `id_intencion=${id}`;
        }

        // Llamada a la API con formato GET
        const response = await callApi(
          `/formulario/formularioObtener?${queryParam}`,
          "get"
        );

        if (response?.formulario) {
          setFormulario(response.formulario);
          setId(response.id_formulario_asignado);
          // Guardar el estado del formulario
          setEstadoFormulario(response.formulario.estado || "pendiente");

          // Inicializar respuestas
          const respuestasIniciales = {};
          response.formulario.preguntas.forEach((pregunta, index) => {
            if (pregunta.tipo_pregunta === "seleccion_multiple") {
              respuestasIniciales[`pregunta_${index}`] = [];
            } else {
              respuestasIniciales[`pregunta_${index}`] = "";
            }
          });
          setRespuestas(respuestasIniciales);
        } else {
          setError("No se pudo obtener el formulario solicitado");
        }
      } catch (err) {
        console.error("Error al obtener formulario:", err);
        setError(
          "Hubo un error al cargar el formulario. Por favor, intenta nuevamente."
        );
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      obtenerFormulario();
    }
  }, [id]);

  // Manejador para cambios en respuestas de selección única
  const handleSingleChange = (event, index, opcion) => {
    // Usar el orden como identificador único para la opción seleccionada
    setRespuestas({
      ...respuestas,
      [`pregunta_${index}`]: opcion.orden.toString(),
    });
  };

  // Manejador para cambios en respuestas de texto libre
  const handleTextChange = (event, index) => {
    setRespuestas({
      ...respuestas,
      [`pregunta_${index}`]: event.target.value,
    });
  };

  // Manejador para cambios en respuestas de selección múltiple
  // Solución: Usar el orden como identificador único para cada opción
  const handleMultipleChange = (event, index, opcion) => {
    const isChecked = event.target.checked;
    const currentResponses = respuestas[`pregunta_${index}`] || [];

    // Usar simplemente el orden como identificador único
    const opcionId = opcion.orden;

    let newResponses;
    if (isChecked) {
      // Agregar la opción si está seleccionada
      newResponses = [...currentResponses, opcionId];
    } else {
      // Quitar la opción si está deseleccionada
      newResponses = currentResponses.filter((item) => item !== opcionId);
    }

    setRespuestas({
      ...respuestas,
      [`pregunta_${index}`]: newResponses,
    });
  };

  // Función auxiliar para verificar si una opción está seleccionada
  const isOpcionSeleccionada = (index, opcion) => {
    const respuesta = respuestas[`pregunta_${index}`];
    if (!Array.isArray(respuesta)) return false;

    return respuesta.includes(opcion.orden);
  };

  // Validar si una pregunta ha sido respondida
  const isPreguntaRespondida = (index, pregunta) => {
    const respuesta = respuestas[`pregunta_${index}`];

    if (pregunta.tipo_pregunta === "seleccion_multiple") {
      return Array.isArray(respuesta) && respuesta.length > 0;
    } else if (pregunta.tipo_pregunta === "seleccion_unica") {
      // Para selección única, verificamos que exista un valor numérico (orden)
      return respuesta !== undefined && respuesta !== "";
    }

    return respuesta && respuesta.trim() !== "";
  };

  // Validar si todas las preguntas obligatorias están respondidas
  const isFormularioValido = () => {
    if (!formulario || !formulario.preguntas) return false;

    return formulario.preguntas.every((pregunta, index) => {
      // Si la pregunta es obligatoria, debe estar respondida
      return !pregunta.obligatorio || isPreguntaRespondida(index, pregunta);
    });
  };

  // Enviar respuestas
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!isFormularioValido()) {
      setError("Por favor, completa todas las preguntas obligatorias");
      return;
    }

    try {
      setEnviando(true);
      setError(null);

      // Formatear respuestas para enviar a la API según la estructura requerida
      const preguntasConRespuestas = formulario.preguntas.map(
        (pregunta, index) => {
          const respuestaPregunta = respuestas[`pregunta_${index}`];

          // Base de la pregunta con respuesta
          const preguntaFormateada = {
            texto_pregunta: pregunta.texto_pregunta,
            tipo_pregunta: pregunta.tipo_pregunta,
            obligatorio: pregunta.obligatorio ? 1 : 0,
            orden: index + 1,
          };

          // Agregar campos específicos según el tipo de pregunta
          if (pregunta.tipo_pregunta === "abierta") {
            preguntaFormateada.texto_respuesta = respuestaPregunta;
          } else if (pregunta.tipo_pregunta === "seleccion_unica") {
            preguntaFormateada.opciones = pregunta.opciones.map((opcion) => {
              return {
                texto_opcion: opcion.texto_opcion,
                orden: opcion.orden,
                seleccionada: parseInt(respuestaPregunta) === opcion.orden,
              };
            });
          } else if (pregunta.tipo_pregunta === "seleccion_multiple") {
            preguntaFormateada.opciones = pregunta.opciones.map((opcion) => {
              // Verificar si el orden de la opción está en las respuestas seleccionadas
              return {
                texto_opcion: opcion.texto_opcion,
                orden: opcion.orden,
                seleccionada: respuestaPregunta.includes(opcion.orden),
              };
            });
          }

          return preguntaFormateada;
        }
      );

      // Crear objeto de datos para el envío con la estructura requerida
      const datosEnvio = {
        id_formulario_asignado,
        formulario: {
          nombre_formulario: formulario.nombre_formulario,
          descripcion_formulario: formulario.descripcion_formulario,
          preguntas: preguntasConRespuestas,
        },
      };

      // Agregar el identificador correcto
      if (
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          id
        )
      ) {
        datosEnvio.transferencia_id = id;
      } else if (id.startsWith("pref_")) {
        datosEnvio.preference_id = id;
      } else {
        datosEnvio.id_intencion = id;
      }

      console.log("Datos a enviar:", JSON.stringify(datosEnvio, null, 2));

      // Llamada a la API para guardar respuestas
      const response = await callApi(
        "/formulario/formularioGuardar",
        "post",
        datosEnvio
      );

      if (response?.statusCode === 200) {
        setExito(true);
      } else {
        // Mostrar el mensaje de error que viene de la API
        setError(
          response?.message ||
            "No se pudieron guardar tus respuestas. Por favor, intenta nuevamente."
        );
      }
    } catch (error) {
      console.error("Error al enviar respuestas:", error);
      // Si hay un error en la llamada, intentar obtener el mensaje del error
      const errorMsg =
        error.response?.data?.message ||
        "Hubo un error al enviar tus respuestas. Por favor, intenta nuevamente.";
      setError(errorMsg);
    } finally {
      setEnviando(false);
    }
  };

  // Componente de mensaje de éxito para cuando se envía el formulario correctamente
  const SuccessMessage = ({ message }) => {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
          paddingTop: 6,
          paddingBottom: 6,
        }}
      >
        <Link href="/" underline="none" color="inherit" sx={{ mb: 4 }}>
          <img
            src={logo}
            alt="Logo Clinifica"
            style={{
              height: !isMobile ? "60px" : "30px",
            }}
          />
        </Link>
        <Box
          sx={{
            width: "100%",
            maxWidth: 600,
            backgroundColor: "#e8f5e9",
            borderRadius: 2,
            p: 2,
            display: "flex",
            alignItems: "center",
          }}
        >
          <Box sx={{ color: "#4caf50", mr: 2, display: "flex" }}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
          </Box>
          <Typography
            sx={{
              fontFamily: "Quicksand-Regular",
              color: "#2e7d32",
            }}
          >
            {message}
          </Typography>
        </Box>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "80vh",
        }}
      >
        <CircularProgress size={60} sx={{ color: "#8753b1" }} />
        <Typography
          sx={{
            mt: 2,
            fontFamily: "Quicksand-Regular",
            fontSize: "18px",
            color: "#4F4F4F",
          }}
        >
          Cargando formulario...
        </Typography>
      </Box>
    );
  }

  if (error && !formulario) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "80vh",
          p: 3,
        }}
      >
        <Alert
          severity="error"
          sx={{
            width: "100%",
            maxWidth: 600,
            borderRadius: 2,
          }}
        >
          {error}
        </Alert>
        <Button
          variant="outlined"
          sx={{
            mt: 3,
            fontFamily: "Quicksand-Bold",
            borderRadius: 30,
            textTransform: "none",
          }}
          onClick={() => window.location.reload()}
        >
          Reintentar
        </Button>
      </Box>
    );
  }

  // Mostrar mensaje de éxito si el formulario fue enviado correctamente
  if (exito) {
    return (
      <SuccessMessage message="¡Gracias por completar el formulario! Tus respuestas han sido enviadas correctamente." />
    );
  }

  // Mostrar mensaje si el formulario ya ha sido completado anteriormente
  if (estadoFormulario === "completado") {
    return (
      <FormContainer>
        <FormHeader>
          <Link href="/" underline="none" color="inherit">
            <img
              src={logo}
              alt="Logo Clinifica"
              style={{
                height: !isMobile ? "60px" : "30px",
              }}
            />
          </Link>
          <Typography
            variant="h5"
            sx={{
              fontFamily: "Quicksand-Bold",
              color: "#4F4F4F",
              textAlign: "center",
              mt: 3,
            }}
          >
            {formulario?.nombre_formulario || "Formulario"}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontFamily: "Quicksand-Regular",
              color: "#757575",
              textAlign: "center",
              mt: 1,
            }}
          >
            {formulario?.descripcion_formulario || ""}
          </Typography>
        </FormHeader>

        {/* Mensaje de formulario completado */}
        <Box
          sx={{
            width: "100%",
            backgroundColor: "#e8f5e9",
            borderRadius: 2,
            mb: 3,
            display: "flex",
            alignItems: "center",
          }}
        >
          <Box sx={{ color: "#4caf50", mr: 2, ml: 2, display: "flex" }}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
          </Box>
          <Typography
            sx={{
              fontFamily: "Quicksand-Regular",
              color: "#2e7d32",
              p: 2,
            }}
          >
            Ya has enviado este formulario. Gracias por tu respuesta.
          </Typography>
        </Box>

        {/* Mostrar formulario con las respuestas en modo solo lectura */}
        {formulario?.preguntas?.map((pregunta, index) => (
          <QuestionContainer key={index}>
            <Typography
              sx={{
                fontFamily: "Quicksand-Bold",
                color: "#4F4F4F",
                mb: 2,
              }}
            >
              {index + 1}. {pregunta.texto_pregunta}
              {pregunta.obligatorio && (
                <RequiredIndicator> *</RequiredIndicator>
              )}
            </Typography>

            {/* Respuesta de pregunta abierta */}
            {pregunta.tipo_pregunta === "abierta" && (
              <Typography
                sx={{
                  fontFamily: "Quicksand-Regular",
                  color: "#4F4F4F",
                  p: 2,
                  bgcolor: "#f5f5f5",
                  borderRadius: 1,
                }}
              >
                {pregunta.texto_respuesta || "Sin respuesta"}
              </Typography>
            )}

            {/* Respuesta de pregunta de selección única */}
            {pregunta.tipo_pregunta === "seleccion_unica" && (
              <FormControl component="fieldset" fullWidth>
                <RadioGroup>
                  {pregunta.opciones?.map((opcion, opcionIndex) => (
                    <FormControlLabel
                      key={opcionIndex}
                      value={opcion.orden.toString()}
                      control={
                        <Radio
                          checked={opcion.seleccionada === 1}
                          disabled={true}
                          sx={{
                            color: "#8753b1",
                            "&.Mui-checked": {
                              color: "#8753b1",
                            },
                          }}
                        />
                      }
                      label={
                        <Typography
                          sx={{
                            fontFamily: "Quicksand-Regular",
                            color: "#4F4F4F",
                            fontWeight:
                              opcion.seleccionada === 1 ? "bold" : "normal",
                          }}
                        >
                          {opcion.texto_opcion}
                        </Typography>
                      }
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            )}

            {/* Respuesta de pregunta de selección múltiple */}
            {pregunta.tipo_pregunta === "seleccion_multiple" && (
              <FormControl component="fieldset" fullWidth>
                {pregunta.opciones?.map((opcion, opcionIndex) => (
                  <FormControlLabel
                    key={opcionIndex}
                    control={
                      <Checkbox
                        checked={opcion.seleccionada === 1}
                        disabled={true}
                        sx={{
                          color: "#8753b1",
                          "&.Mui-checked": {
                            color: "#8753b1",
                          },
                        }}
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          fontFamily: "Quicksand-Regular",
                          color: "#4F4F4F",
                          fontWeight:
                            opcion.seleccionada === 1 ? "bold" : "normal",
                        }}
                      >
                        {opcion.texto_opcion}
                      </Typography>
                    }
                  />
                ))}
              </FormControl>
            )}
          </QuestionContainer>
        ))}
      </FormContainer>
    );
  }

  return (
    <FormContainer>
      <FormHeader>
        <Link href="/" underline="none" color="inherit">
          <img
            src={logo}
            alt="Logo Clinifica"
            style={{
              height: !isMobile ? "60px" : "30px",
            }}
          />
        </Link>
        <Typography
          variant="h5"
          sx={{
            fontFamily: "Quicksand-Bold",
            color: "#4F4F4F",
            textAlign: "center",
            mt: 3,
          }}
        >
          {formulario?.nombre_formulario || "Formulario"}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontFamily: "Quicksand-Regular",
            color: "#757575",
            textAlign: "center",
            mt: 1,
          }}
        >
          {formulario?.descripcion_formulario || ""}
        </Typography>
      </FormHeader>

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: 2,
          }}
        >
          {error}
        </Alert>
      )}

      {formulario?.preguntas?.map((pregunta, index) => (
        <QuestionContainer key={index}>
          <Typography
            sx={{
              fontFamily: "Quicksand-Bold",
              color: "#4F4F4F",
              mb: 2,
            }}
          >
            {index + 1}. {pregunta.texto_pregunta}
            {pregunta.obligatorio && <RequiredIndicator> *</RequiredIndicator>}
          </Typography>

          {pregunta.tipo_pregunta === "abierta" && (
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Escribe tu respuesta aquí"
              variant="outlined"
              value={respuestas[`pregunta_${index}`] || ""}
              onChange={(e) => handleTextChange(e, index)}
              error={
                pregunta.obligatorio && !isPreguntaRespondida(index, pregunta)
              }
              helperText={
                pregunta.obligatorio &&
                !isPreguntaRespondida(index, pregunta) &&
                "Esta pregunta es obligatoria"
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  fontFamily: "Quicksand-Regular",
                },
              }}
            />
          )}

          {pregunta.tipo_pregunta === "seleccion_unica" && (
            <FormControl
              component="fieldset"
              error={
                pregunta.obligatorio && !isPreguntaRespondida(index, pregunta)
              }
              fullWidth
            >
              <RadioGroup value={respuestas[`pregunta_${index}`] || ""}>
                {pregunta.opciones?.map((opcion, opcionIndex) => (
                  <FormControlLabel
                    key={opcionIndex}
                    value={opcion.orden.toString()}
                    control={
                      <Radio
                        checked={
                          parseInt(respuestas[`pregunta_${index}`]) ===
                          opcion.orden
                        }
                        onChange={() => {
                          setRespuestas({
                            ...respuestas,
                            [`pregunta_${index}`]: opcion.orden.toString(),
                          });
                        }}
                        sx={{
                          color: "#8753b1",
                          "&.Mui-checked": {
                            color: "#8753b1",
                          },
                        }}
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          fontFamily: "Quicksand-Regular",
                          color: "#4F4F4F",
                        }}
                      >
                        {opcion.texto_opcion}
                      </Typography>
                    }
                  />
                ))}
              </RadioGroup>
              {pregunta.obligatorio &&
                !isPreguntaRespondida(index, pregunta) && (
                  <Typography
                    sx={{
                      color: "#d32f2f",
                      fontFamily: "Quicksand-Regular",
                      fontSize: "0.75rem",
                      mt: 1,
                    }}
                  >
                    Esta pregunta es obligatoria
                  </Typography>
                )}
            </FormControl>
          )}

          {pregunta.tipo_pregunta === "seleccion_multiple" && (
            <FormControl
              component="fieldset"
              error={
                pregunta.obligatorio && !isPreguntaRespondida(index, pregunta)
              }
              fullWidth
            >
              {pregunta.opciones?.map((opcion, opcionIndex) => (
                <FormControlLabel
                  key={opcionIndex}
                  control={
                    <Checkbox
                      checked={isOpcionSeleccionada(index, opcion)}
                      onChange={(e) => handleMultipleChange(e, index, opcion)}
                      sx={{
                        color: "#8753b1",
                        "&.Mui-checked": {
                          color: "#8753b1",
                        },
                      }}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        fontFamily: "Quicksand-Regular",
                        color: "#4F4F4F",
                      }}
                    >
                      {opcion.texto_opcion}
                    </Typography>
                  }
                />
              ))}
              {pregunta.obligatorio &&
                !isPreguntaRespondida(index, pregunta) && (
                  <Typography
                    sx={{
                      color: "#d32f2f",
                      fontFamily: "Quicksand-Regular",
                      fontSize: "0.75rem",
                      mt: 1,
                    }}
                  >
                    Esta pregunta es obligatoria
                  </Typography>
                )}
            </FormControl>
          )}
        </QuestionContainer>
      ))}

      <SubmitButton
        onClick={handleSubmit}
        disabled={enviando || !isFormularioValido()}
      >
        {enviando ? <CircularProgress size={24} color="inherit" /> : "Enviar"}
      </SubmitButton>
    </FormContainer>
  );
};

export default FormularioRespuesta;

